import { modals } from '@mantine/modals'
import { useEffect, useLayoutEffect, useRef } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'

interface UseTabNavigationWarningOptions {
  /**
   * Function that returns true if the panel has unsaved changes
   */
  hasUnsavedChanges: () => boolean

  /**
   * Function called when user confirms they want to discard changes
   * Use this to reset the panel to its original state
   */
  onDiscard?: () => void

  /**
   * Custom message for the confirmation modal
   */
  message?: string

  /**
   * Custom title for the confirmation modal
   */
  title?: string

  /**
   * Whether the warning is currently enabled
   * Useful for disabling during save operations
   */
  enabled?: boolean
}

/**
 * Hook that intercepts tab navigation and shows a warning if there are unsaved changes.
 * This works by setting a router location change function that gets called before navigation.
 *
 * Each panel can use its own change detection logic while getting consistent navigation behavior.
 */
export function useTabNavigationWarning({
  hasUnsavedChanges,
  onDiscard,
  message = 'You have unsaved changes. Are you sure you want to leave this page?',
  title = 'Unsaved Changes',
  enabled = true,
}: UseTabNavigationWarningOptions) {
  const { uiStore } = useGlobalState()

  // Use refs to store the latest values without causing re-renders
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges)
  const onDiscardRef = useRef(onDiscard)
  const messageRef = useRef(message)
  const titleRef = useRef(title)

  useLayoutEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges
    onDiscardRef.current = onDiscard
    messageRef.current = message
    titleRef.current = title
  })

  useEffect(() => {
    if (!enabled) {
      uiStore.setRouterLocationChangeFn(undefined)
      return
    }

    // Set up router navigation interception (for leaving the entire page)
    uiStore.setRouterLocationChangeFn(async () => {
      // If no unsaved changes, allow navigation
      if (!hasUnsavedChangesRef.current()) {
        return true
      }

      // Create a promise that resolves when user makes a choice
      let resolveFn: ((value: boolean) => void) | undefined

      const confirmation = new Promise<boolean>(resolve => {
        resolveFn = resolve
      })

      // Show the confirmation modal
      modals.openConfirmModal({
        title: titleRef.current,
        centered: true,
        children: messageRef.current,
        labels: { confirm: 'Leave Page', cancel: 'Stay' },
        confirmProps: { color: 'red' },
        groupProps: { mt: 'xl' },
        onConfirm: () => {
          onDiscardRef.current?.()
          resolveFn?.(true)
        },
        onCancel: () => resolveFn?.(false),
      })

      return confirmation
    })

    // Cleanup function
    return () => {
      uiStore.setRouterLocationChangeFn(undefined)
    }
  }, [enabled, uiStore]) // Only depend on enabled and uiStore
}
